/**
 * @file: useLogActivityForm.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The log-activity form state machine: channel + outcome + notes, plus the
 * 已改期 reschedule date. Owns the cross-field rules (outcome cleared when the
 * channel no longer offers it; outcome mandatory unless the channel is 备注)
 * and the submit that records the outreach activity then pops the screen.
 */

import { useLeadMediaUploader } from '@euler/app/flows/leads/functions/useLeadMediaUploader';
import { useLeadDetail } from '@euler/app/flows/leads/pages/detail/functions/useLeadDetail';
import { useAppLoading } from '@euler/components/loading/Provider';
import { LeadActivityOutcome } from '@euler/model/lead/LeadActivity';
import { useServiceFactory } from '@euler/services/factory';
import { dateFromValue } from '@euler/utils/datetime';
import { useNavigation } from '@react-navigation/native';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import { useCallback, useMemo, useState } from 'react';
import Toast from 'react-native-root-toast';
import { SelectableChannel, kOutcomesByChannel } from './channels';

export const useLogActivityForm = (leadId: string) => {
  const { leadService } = useServiceFactory();
  const loading = useAppLoading();
  const navigation = useNavigation();

  const [channel, setChannel] = useState<SelectableChannel>('phone');
  const [outcome, setOutcome] = useState<LeadActivityOutcome | undefined>(
    undefined,
  );
  const [notes, setNotes] = useState('');
  const uploader = useLeadMediaUploader();

  const outcomes = kOutcomesByChannel[channel];
  // Outcome is mandatory for every channel that offers one (all but 备注,
  // whose follow-up is just a note) — block the save until it's picked, and
  // while any attachment is still uploading or has failed (a failed tile is
  // dropped from the payload otherwise).
  const canSubmit =
    (outcomes.length === 0 || outcome != null) &&
    !uploader.uploading &&
    !uploader.hasError;

  // 已改期 reveals a target-service-date picker that reschedules the lead.
  // Default to the lead's current target (fetched lazily) so the advisor nudges
  // it rather than scrolling from today; falls back to a week out.
  const lead = useLeadDetail(leadId).state?.lead;
  const [postponeAt, setPostponeAt] = useState<Date | null>(null);
  const defaultPostpone = useMemo(() => {
    if (lead?.targetServiceDate) {
      try {
        return dateFromValue(lead.targetServiceDate);
      } catch {
        /* fall through */
      }
    }
    return addDays(new Date(), 7);
  }, [lead?.targetServiceDate]);
  const postponeDate = postponeAt ?? defaultPostpone;
  const isPostponed = outcome === 'postponed';

  // Switching channels keeps a still-valid outcome but drops one the new
  // channel doesn't offer (e.g. 未接听 after moving phone → 微信).
  const onChannelChange = useCallback((next: SelectableChannel) => {
    setChannel(next);
    setOutcome(prev =>
      prev && kOutcomesByChannel[next].includes(prev) ? prev : undefined,
    );
  }, []);

  const toggleOutcome = useCallback((o: LeadActivityOutcome) => {
    setOutcome(prev => (prev === o ? undefined : o));
  }, []);

  const onSubmit = useCallback(async () => {
    await loading.showWithAction(async () => {
      const attachments = uploader.collectUploaded();
      await leadService.logActivity(leadId, {
        channel,
        outcome: outcome ?? null,
        notes: notes.trim() || null,
        rescheduleTo: isPostponed ? format(postponeDate, 'yyyy-MM-dd') : null,
        attachments: attachments.length ? attachments : undefined,
      });
      Toast.show('已记录跟进', { position: Toast.positions.BOTTOM });
      navigation.goBack();
    });
  }, [
    channel,
    isPostponed,
    leadService,
    leadId,
    loading,
    navigation,
    notes,
    outcome,
    postponeDate,
    uploader,
  ]);

  return {
    channel,
    onChannelChange,
    outcomes,
    outcome,
    toggleOutcome,
    isPostponed,
    postponeDate,
    setPostponeAt,
    notes,
    setNotes,
    uploader,
    canSubmit,
    onSubmit,
  };
};
